// Authenticated home: mounts the singleton socket, primes the agent list,
// and renders the DashboardLayout.
//
// Sprint UI-1: the sidebar is now a single sectioned list (Layer 5 D14),
// so we drop the active/archived view toggle and load every agent once
// on mount. Live updates after that arrive via WebSocket.
//
// Sprint 5.6 — templates were removed (ADR D-001), so the templates
// pre-load from earlier sprints is gone too.

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { DocViewerSheet } from '../components/DocViewerSheet';
import { useSocket } from '../hooks/useSocket';
import { useDeepLink } from '../hooks/useDeepLink';
import { useAppStore } from '../stores/app.store';
import { useSettingsStore } from '../stores/settings.store';
import { useServersStore } from '../stores/servers.store';
import { startRunnersPolling } from '../stores/runners.store';
import { startRunnersUsagePolling } from '../stores/runners-usage.store';
import { useStagesStore } from '../stores/stages.store';
import { useActiveWorkspaceStore } from '../stores/active-workspace.store';

export interface HomePageProps {
  logout: () => Promise<void>;
}

export function HomePage({ logout }: HomePageProps) {
  const navigate = useNavigate();
  const loadAgents = useAppStore((s) => s.loadAgents);
  const loadServerInfo = useAppStore((s) => s.loadServerInfo);
  const hydrateSettings = useSettingsStore((s) => s.hydrate);
  useSocket();
  // Sprint 7 (Phase 8.3) — handle Telegram deep-links (`#agent=<id>`)
  // exactly once per page-load. Mounted here so it runs only after the
  // auth gate passes.
  useDeepLink();

  // Sprint stages-per-project — stages are scoped to the active workspace
  // and reload whenever it changes (same reactive source the agent filter
  // already uses). Failure degrades to "Sin estado" + "Archivado" only.
  const activeWorkspaceIdForStages = useActiveWorkspaceStore((s) => s.activeWorkspaceId);
  useEffect(() => {
    void useStagesStore.getState().load(activeWorkspaceIdForStages);
  }, [activeWorkspaceIdForStages]);

  useEffect(() => {
    loadAgents().catch(() => undefined);
    // Sprint UI-7b (D76) — fetch server identity for the ChatHeader chip.
    // Fire-and-forget: if it fails, the chip falls back to its default.
    void loadServerInfo();
    // S11b hotfix (H6 follow-up) — refresh the live status of every
    // registered backend on app mount so the ServerSelector dot is green
    // (online) or red (offline) instead of yellow (unknown). Status is
    // not persisted in localStorage (see servers.store partialize), so
    // every fresh page load starts in 'unknown' until a probe runs.
    void useServersStore.getState().probeAll();
  }, [loadAgents, loadServerInfo]);

  // Sprint working-signal-ssot (D013-EC-138, spec D6) — a backgrounded
  // tab (phone PWA especially) misses agent:updated events silently.
  // Foregrounding reconciles the list; the ≥10 s throttle lives inside
  // the store action.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void useAppStore.getState().refetchAgentsThrottled();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Sprint 6 (D47) — hydrate the global settings (developerMode, telegram
  // prefs, sound) once on mount. Failures fall back to defaults silently.
  useEffect(() => {
    void hydrateSettings();
  }, [hydrateSettings]);

  // Sprint 20.3 PR-9 (D20 / UC-38) — runners store polling. The
  // ChatHeader's `RunnerChip` reads from this store; mounting the
  // polling here (vs per-chip) keeps the network at one fetch every
  // 30 s regardless of how many chips/agents are open. Hidden tabs
  // are skipped automatically (visibilitychange guard inside
  // startRunnersPolling).
  useEffect(() => {
    return startRunnersPolling();
  }, []);

  // Sprint runner-ssot-unification PR-1 (INV-X) — authoritative usage
  // cache polling. 60 s cadence mirrors the backend cache TTL. Both
  // the pill and the modal read from this store.
  useEffect(() => {
    return startRunnersUsagePolling();
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <>
      <DashboardLayout onLogout={handleLogout} />
      <DocViewerSheet />
    </>
  );
}
